import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import EditOffIcon from '@mui/icons-material/EditOff'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import RedoIcon from '@mui/icons-material/Redo'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import UndoIcon from '@mui/icons-material/Undo'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import { Box, Button, Chip, Dialog, Divider, IconButton, Toolbar, Tooltip, Typography } from '@mui/material'
import { DocumentTypes, FA_CLASSIFICATION_MAPPING, IFP_CLASSIFICATION_MAPPING } from '@src/types/constants'
import { DocumentPage } from '@src/types/documentPage'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const DOC_TYPE_GROUPS: { labelKey: string; types: string[] }[] = [
  {
    labelKey: 'Iceberg',
    types: [
      DocumentTypes.Contract,
      DocumentTypes.Dcc,
      DocumentTypes.Engagement,
      DocumentTypes.RepayCapacity,
      DocumentTypes.Annex,
      DocumentTypes.CreditFile,
      DocumentTypes.Reference,
    ],
  },
  {
    labelKey: 'Client',
    types: [
      DocumentTypes.Paystub,
      DocumentTypes.ProofIncome,
      DocumentTypes.VoidCheck,
      DocumentTypes.BankStatement,
      DocumentTypes.DriverLicence,
      DocumentTypes.IdPhoto,
      DocumentTypes.WorkPermit,
      DocumentTypes.SignatureAudit,
    ],
  },
  {
    labelKey: 'common.miscellaneous',
    types: [
      DocumentTypes.Bankruptcy,
      DocumentTypes.Carfax,
      DocumentTypes.Registration,
      DocumentTypes.WindowSticker,
      DocumentTypes.Other,
    ],
  },
]

interface PageZoomDialogProps {
  selectedPage: DocumentPage | null
  filteredPages: DocumentPage[]
  pageHeader: (page: DocumentPage) => string
  assignDocumentTypeToPage?: (page: DocumentPage) => string | null
  assignDocumentTypeIsOverridden?: (page: DocumentPage) => boolean
  onClose: () => void
  onNavigate: (page: DocumentPage) => void
  canEdit?: boolean
  sentPageIds?: Set<string>
  onChangeDocType?: (page: DocumentPage, documentTypeId?: string) => void
  onDelete?: (page: DocumentPage) => void
  onRotate?: (pageId: string, degrees: number) => void
  productLine?: number
  canUndo?: boolean
  canRedo?: boolean
  onUndo?: () => void
  onRedo?: () => void
}

export const PageZoomDialog = ({
  selectedPage,
  filteredPages,
  pageHeader,
  assignDocumentTypeToPage,
  assignDocumentTypeIsOverridden,
  onClose,
  onNavigate,
  canEdit = false,
  sentPageIds,
  onChangeDocType,
  onDelete,
  onRotate,
  productLine,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
}: PageZoomDialogProps) => {
  const { t } = useTranslation()
  const [editMode, setEditMode] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)

  const dragStateRef = useRef({ isDragging: false, startX: 0, startY: 0, lastX: 0, lastY: 0 })
  const imageRef = useRef<HTMLImageElement>(null)
  const animationFrameRef = useRef<number | undefined>(undefined)
  const containerRef = useRef<HTMLDivElement>(null)
  const docTypePanelRef = useRef<HTMLDivElement>(null)

  // Reset zoom and position when page changes
  useEffect(() => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
    setIsDragging(false)
    dragStateRef.current = { isDragging: false, startX: 0, startY: 0, lastX: 0, lastY: 0 }
  }, [selectedPage?.id])

  // Attach wheel listener as non-passive to allow preventDefault
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleWheel = (e: WheelEvent) => {
      // Don't intercept scroll on the quick type panel
      if (docTypePanelRef.current?.contains(e.target as Node)) return

      e.preventDefault()
      e.stopPropagation()
      const step = Math.min(Math.abs(e.deltaY) / 500, 0.2)
      if (e.deltaY < 0) {
        setZoom((prev) => Math.min(prev + step, 5))
      } else {
        setZoom((prev) => Math.max(prev - step, 0.5))
      }
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  })

  if (!selectedPage) return null

  const isPageSent = sentPageIds?.has(selectedPage.id) ?? false
  const canEditPage = canEdit && !isPageSent

  const assigned = assignDocumentTypeToPage?.(selectedPage) ?? null
  const headerValue = pageHeader(selectedPage)
  const isOverridden = assignDocumentTypeIsOverridden?.(selectedPage) ?? false

  const currentIndex = filteredPages.findIndex((p) => p.id === selectedPage.id)
  const canGoPrevious = currentIndex > 0
  const canGoNext = currentIndex < filteredPages.length - 1

  const handleToggleEditMode = () => {
    setEditMode(!editMode)
  }

  const handleZoomIn = (step = 0.1) => {
    setZoom((prev) => Math.min(prev + step, 5))
  }

  const handleZoomOut = (step = 0.1) => {
    setZoom((prev) => Math.max(prev - step, 0.5))
  }

  const handleResetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      e.preventDefault()
      setIsDragging(true)
      dragStateRef.current = {
        isDragging: true,
        startX: e.clientX - position.x,
        startY: e.clientY - position.y,
        lastX: e.clientX,
        lastY: e.clientY,
      }
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragStateRef.current.isDragging || zoom <= 1) return

    e.preventDefault()

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      const newX = e.clientX - dragStateRef.current.startX
      const newY = e.clientY - dragStateRef.current.startY

      setPosition({ x: newX, y: newY })

      dragStateRef.current.lastX = e.clientX
      dragStateRef.current.lastY = e.clientY
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    dragStateRef.current.isDragging = false
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
  }

  const handleChangeDocType = () => {
    if (onChangeDocType) {
      onChangeDocType(selectedPage)
    }
  }

  const handleQuickChangeDocType = (docTypeName: string) => {
    if (onChangeDocType && productLine !== undefined) {
      const mapping = productLine === 0 ? FA_CLASSIFICATION_MAPPING : IFP_CLASSIFICATION_MAPPING
      const documentTypeId = mapping[docTypeName]
      if (documentTypeId) {
        onChangeDocType(selectedPage, documentTypeId.toString())
        if (canGoNext) {
          onNavigate(filteredPages[currentIndex + 1])
        }
      }
    }
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete(selectedPage)
    }
  }

  const handleRotateLeft = () => {
    if (onRotate) {
      onRotate(selectedPage.id, -90)
    }
  }

  const handleRotateRight = () => {
    if (onRotate) {
      onRotate(selectedPage.id, 90)
    }
  }

  const handlePrevious = () => {
    if (canGoPrevious) {
      onNavigate(filteredPages[currentIndex - 1])
    }
  }

  const handleNext = () => {
    if (canGoNext) {
      onNavigate(filteredPages[currentIndex + 1])
    }
  }

  return (
    <Dialog open={!!selectedPage} onClose={onClose} maxWidth="xl" fullWidth fullScreen>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box>
              {assigned && isOverridden && <Typography variant="caption">{assigned} / </Typography>}
              <Typography variant="caption" color={isOverridden ? 'error' : undefined}>
                {headerValue}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              ({currentIndex + 1} / {filteredPages.length})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Zoom In">
              <IconButton onClick={() => handleZoomIn(0.25)} disabled={zoom >= 5}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ minWidth: 50, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom Out">
              <IconButton onClick={() => handleZoomOut(0.25)} disabled={zoom <= 0.5}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset Zoom">
              <IconButton onClick={handleResetZoom} disabled={zoom === 1 && position.x === 0 && position.y === 0}>
                <ZoomOutMapIcon />
              </IconButton>
            </Tooltip>
            {canEditPage && (
              <>
                <Tooltip title={t('common.rotateLeft')}>
                  <IconButton onClick={handleRotateLeft}>
                    <RotateLeftIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.rotateRight')}>
                  <IconButton onClick={handleRotateRight}>
                    <RotateRightIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {canEditPage && (
              <Tooltip title={editMode ? t('common.edit') : t('common.edit')}>
                <IconButton onClick={handleToggleEditMode} color={editMode ? 'primary' : 'default'}>
                  {editMode ? <EditOffIcon /> : <EditIcon />}
                </IconButton>
              </Tooltip>
            )}
            {editMode && canEditPage && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip title={t('common.undo')}>
                  <span>
                    <IconButton onClick={onUndo} disabled={!canUndo}>
                      <UndoIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('common.redo')}>
                  <span>
                    <IconButton onClick={onRedo} disabled={!canRedo}>
                      <RedoIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip title={t('common.delete')}>
                  <IconButton onClick={handleDelete} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={t('common.close')}>
              <IconButton onClick={onClose} edge="end">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>

        <Box ref={containerRef} sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Image area */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'transparent',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Left navigation button */}
            {canGoPrevious && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                }}
              >
                <Tooltip title={t('common.previous')}>
                  <IconButton
                    onClick={handlePrevious}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    <NavigateBeforeIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Right navigation button */}
            {canGoNext && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                }}
              >
                <Tooltip title={t('common.next')}>
                  <IconButton
                    onClick={handleNext}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    <NavigateNextIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Image */}
            {selectedPage.storageUrl ? (
              <Box
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  overflow: 'hidden',
                }}
              >
                <img
                  ref={imageRef}
                  src={selectedPage.storageUrl}
                  alt={pageHeader(selectedPage)}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `translate(${position.x}px, ${position.y}px) scale(${zoom}) rotate(${selectedPage.rotation || 0}deg)`,
                    transformOrigin: 'center',
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    willChange: isDragging ? 'transform' : 'auto',
                  }}
                  draggable={false}
                />
              </Box>
            ) : (
              <Typography color="text.secondary">{t('common.noImageAvailable')}</Typography>
            )}
          </Box>

          {/* Quick Document Type Selection - Right Side Panel */}
          {editMode && canEditPage && (
            <Box
              ref={docTypePanelRef}
              sx={{
                width: { xs: 180, md: 220 },
                flexShrink: 0,
                borderLeft: 1,
                borderColor: 'divider',
                overflowY: 'auto',
                p: 2,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 'bold',
                  letterSpacing: 1,
                  display: 'block',
                  mb: 0.5,
                  textAlign: 'center',
                  color: 'primary.main',
                }}
              >
                {t('common.quickSelect')}
              </Typography>
              {DOC_TYPE_GROUPS.map((group) => (
                <Box key={group.labelKey} sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {group.labelKey.includes('.') ? t(group.labelKey as never) : group.labelKey}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {group.types.map((docType) => (
                      <Chip
                        key={docType}
                        label={t(`enum.documentTypes.${docType}` as never)}
                        onClick={() => handleQuickChangeDocType(docType)}
                        size="small"
                        clickable
                        variant="outlined"
                        sx={{
                          width: '100%',
                          justifyContent: 'flex-start',
                          '&:hover': {
                            color: 'primary.main',
                            borderColor: 'primary.main',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
              <Divider sx={{ mb: 1.5 }} />
              <Button variant="outlined" startIcon={<EditIcon />} onClick={handleChangeDocType} size="small" fullWidth>
                {t('common.changeDocumentType')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  )
}
